
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const navigate = useNavigate();

  // Simplified fallback function - only used as absolute last resort
  const ensureUserProfileFallback = async (userId: string, userData: any) => {
    try {
      console.log('🔧 Fallback: Checking if user profile exists for:', userId);
      
      // Wait longer to let database trigger complete first
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Check if profile exists
      const { data: existingProfile, error: checkError } = await supabase
        .from('user_profiles')
        .select('user_id')
        .eq('user_id', userId)
        .single();

      if (checkError && checkError.code === 'PGRST116') {  // Not found error
        console.log('🔧 Fallback: Profile not found, creating via fallback...');
        
        // Create user profile as fallback only if trigger failed
        const { error: insertError } = await supabase
          .from('user_profiles')
          .insert({
            user_id: userId,
            display_name: userData?.user_metadata?.name || userData?.email,
            display_email: userData?.email
          });
          
        if (insertError && insertError.code !== '23505') { // Ignore duplicate key errors
          console.error('❌ Fallback profile creation failed:', insertError);
        } else {
          console.log('✅ Fallback profile creation successful');
        }
      } else if (checkError) {
        console.error('❌ Error checking profile in fallback:', checkError);
      } else {
        console.log('✅ Profile already exists, no fallback needed');
      }
    } catch (error) {
      console.error('❌ Unexpected error in profile fallback:', error);
    }
  };

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session:', error);
        } else {
          setSession(data.session);
          setUser(data.session?.user || null);
        }
      } catch (error) {
        console.error('Unexpected error during getSession:', error);
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Auth state change:', event);
        setSession(session);
        setUser(session?.user || null);
        setIsLoading(false);
        
        // Only use fallback for new sign-ups as absolute last resort
        if (session?.user && event === 'SIGNED_IN' && !isInitialLoad) {
          // Wait longer and then check if profile exists (fallback only)
          setTimeout(() => {
            ensureUserProfileFallback(session.user.id, session.user);
          }, 5000); // Wait 5 seconds to let database trigger complete first
        }
        
        // Show toast for actual events, not session restoration
        if (event === 'SIGNED_IN' && !isInitialLoad) {
          toast.success("Successfully logged in!");
        } else if (event === 'SIGNED_OUT') {
          toast.success("Successfully logged out!");
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [isInitialLoad]);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/auth/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    session,
    user,
    isLoading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
